import { useMutation } from "@apollo/client";
import { useState } from "react";

import { graphql } from "../generated/gql";
import {
  LayoutStatus,
  UpdateLayoutInput,
  UpdateLayoutMutation,
  UpdateLayoutMutationVariables,
} from "../generated/gql/graphql";

import useNotification from "./use-notification";
import { ArrayElement } from "./utils";

type LayoutApi = {
  renameLayoutById(id: string, name: string): Promise<void>;
  archiveLayoutById(id: string): Promise<void>;
};

export type UpdatedLayout = UpdateLayoutMutation["updateLayout"];
export type UpdateRenderingObjectProductInput = ArrayElement<
  ArrayElement<UpdateLayoutInput["renderingObjects"]>["productInputs"]
>;

const UPDATE_LAYOUT = graphql(`
  mutation UpdateLayout($updateLayoutId: ID!, $data: UpdateLayoutInput!) {
    updateLayout(id: $updateLayoutId, data: $data) {
      name
    }
  }
`);

export default function useUpdateLayout(): LayoutApi {
  const notification = useNotification();
  const [updateLayout] = useMutation(UPDATE_LAYOUT);

  async function renameLayoutById(
    updateLayoutId: string,
    name: string,
  ): Promise<void> {
    const result = await updateLayout({
      variables: {
        updateLayoutId,
        data: {
          name,
        },
      },
    });

    if (result.errors && result.errors.length > 0) {
      notification.addError("Something was wrong.");
      throw result.errors;
    }
  }

  async function archiveLayoutById(updateLayoutId: string): Promise<void> {
    const result = await updateLayout({
      variables: {
        updateLayoutId,
        data: {
          status: LayoutStatus.Archived,
        },
      },
    });

    if (result.errors && result.errors.length > 0) {
      notification.addError("Something was wrong.");
      throw result.errors;
    }
  }

  return {
    renameLayoutById,
    archiveLayoutById,
  };
}

interface LayoutUpdate {
  data: UpdateLayoutMutationVariables["data"];
  timer: NodeJS.Timeout;
}

export function useUpdateLayoutById(
  layoutId: UpdateLayoutMutationVariables["updateLayoutId"],
): (data: UpdateLayoutMutationVariables["data"]) => void {
  const [updateLayout] = useMutation(UPDATE_LAYOUT);
  const [, setLastUpdate] = useState<LayoutUpdate | null>(null);

  return (data: UpdateLayoutMutationVariables["data"]) => {
    setLastUpdate((prevUpdate) => {
      if (prevUpdate) {
        clearTimeout(prevUpdate.timer);
      }
      const timer = setTimeout(() => {
        updateLayout({
          variables: {
            updateLayoutId: layoutId,
            data,
          },
        });
      }, 1000);
      return { data, timer };
    });
  };
}
